import { httpClient, PaginationParams } from '../core/http';
import {
  CreateToolTemplateRequest,
  ToolTemplate,
  ToolTemplateListItem,
  UpdateToolTemplateRequest,
} from '../types';

export type ToolListResponse = {
  items: ToolTemplateListItem[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
};

export class ToolService {
  private readonly basePath = '/v1/tools';

  async create(payload: CreateToolTemplateRequest): Promise<ToolTemplate> {
    const response = await httpClient.post<ToolTemplate>(this.basePath, payload);
    return response.data;
  }

  async getById(toolId: string): Promise<ToolTemplate> {
    const response = await httpClient.get<ToolTemplate>(`${this.basePath}/${toolId}`);
    return response.data;
  }

  async list(
    params?: PaginationParams & {
      include_hidden?: boolean;
      include_inactive?: boolean;
    }
  ): Promise<ToolListResponse> {
    const response = await httpClient.get<ToolListResponse>(this.basePath, {
      params: params || {},
    });
    return response.data;
  }

  async update(toolId: string, payload: UpdateToolTemplateRequest): Promise<ToolTemplate> {
    const response = await httpClient.put<ToolTemplate>(`${this.basePath}/${toolId}`, payload);
    return response.data;
  }

  async delete(toolId: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${toolId}`);
  }

  async duplicate(toolId: string, name?: string): Promise<ToolTemplate> {
    const response = await httpClient.post<ToolTemplate>(
      `${this.basePath}/${toolId}/duplicate`,
      name ? { name } : {}
    );
    return response.data;
  }

  async listByType(
    toolType: string,
    params?: PaginationParams & {
      include_hidden?: boolean;
    }
  ): Promise<ToolListResponse> {
    return this.list({
      ...(params || {}),
    });
  }

  /**
   * Retorna o documento OpenAPI publico da tool para o owner (JWT-auth).
   * Nao usa X-API-Key — esse endpoint serve apenas a UI do dono. Para o
   * integrador externo, mesma OpenAPI pode ser obtida via /api/v1/apps/{id}/schema
   * (com X-API-Key e scope can_read).
   */
  async getOpenApi(toolId: string): Promise<{
    tool_id: string;
    openapi: Record<string, unknown>;
  }> {
    const response = await httpClient.get<{
      tool_id: string;
      openapi: Record<string, unknown>;
    }>(`${this.basePath}/${toolId}/openapi`);
    return response.data;
  }
}

export const toolService = new ToolService();
